import { supabase } from "@/integrations/supabase/client";
import { EmailTemplate, CreateEmailTemplateData, UpdateEmailTemplateData } from "@/types/emailTemplate";

// Map DB row to our interface (subject_en → subject, body_en → content)
function mapDbRow(row: any): EmailTemplate {
	return {
		id: row.id,
		name: row.name,
		subject: row.subject_en || "",
		content: row.body_en || "",
		description: row.description,
		slug: row.slug,
		category: row.category,
		organization_uuid: row.organization_uuid,
		created_by: row.created_by,
		created_at: row.created_at,
		updated_at: row.updated_at,
	};
}

export class EmailTemplateService {
	static async getAll(): Promise<EmailTemplate[]> {
		const { data, error } = await supabase
			.from('email_templates')
			.select('*')
			.order('created_at', { ascending: false });

		if (error)
			throw new Error(`Failed to fetch email templates: ${error.message}`);

		return (data || []).map(mapDbRow);
	}

	static async getById(id: string): Promise<EmailTemplate | null> {
		const { data, error } = await supabase
			.from('email_templates')
			.select('*')
			.eq('id', id)
			.single();

		if (error) {
			if (error.code === 'PGRST116')
				return null;
			throw new Error(`Failed to fetch email template: ${error.message}`);
		}

		return data ? mapDbRow(data) : null;
	}

	static async create(templateData: CreateEmailTemplateData): Promise<EmailTemplate> {
		const { data: { user } } = await supabase.auth.getUser();
		if (!user)
			throw new Error('User not authenticated');

		const { data: userOrg } = await supabase
			.from('user_organization')
			.select('organization_uuid')
			.eq('user_uuid', user.id)
			.single();

		if (!userOrg)
			throw new Error('User organization not found');

		const slug = templateData.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

		const { data, error } = await supabase
			.from('email_templates')
			.insert({
				name: templateData.name,
				slug,
				subject: templateData.subject,
				subject_en: templateData.subject,
				content: templateData.content,
				body_en: templateData.content,
				description: templateData.description,
				category: 'custom',
				organization_uuid: userOrg.organization_uuid,
				created_by: user.id,
			})
			.select()
			.single();

		if (error)
			throw new Error(`Failed to create email template: ${error.message}`);

		return mapDbRow(data);
	}

	static async update(id: string, templateData: UpdateEmailTemplateData): Promise<EmailTemplate> {
		const updates: any = {};
		if (templateData.name !== undefined) updates.name = templateData.name;
		if (templateData.subject !== undefined) {
			updates.subject = templateData.subject;
			updates.subject_en = templateData.subject;
		}
		if (templateData.content !== undefined) {
			updates.content = templateData.content;
			updates.body_en = templateData.content;
		}
		if (templateData.description !== undefined) updates.description = templateData.description;
		updates.updated_at = new Date().toISOString();

		const { data, error } = await supabase
			.from('email_templates')
			.update(updates)
			.eq('id', id)
			.select()
			.single();

		if (error)
			throw new Error(`Failed to update email template: ${error.message}`);

		return mapDbRow(data);
	}

	static async delete(id: string): Promise<void> {
		const { error } = await supabase
			.from('email_templates')
			.delete()
			.eq('id', id);

		if (error)
			throw new Error(`Failed to delete email template: ${error.message}`);
	}

	static async duplicate(id: string, newName?: string): Promise<EmailTemplate> {
		const template = await this.getById(id);
		if (!template)
			throw new Error('Template not found');

		const duplicatedTemplate: CreateEmailTemplateData = {
			name: newName || `${template.name} (Copy)`,
			subject: template.subject,
			content: template.content,
			description: template.description,
		};

		return this.create(duplicatedTemplate);
	}
}
